/**
 * The child's cartoon, played in the browser.
 *
 * The Zoo cartoon is a CHAIN: rendered videos (the animals, no child in them)
 * alternating with Rive artboards that carry the child — the same rig, the same
 * look, the same colours and hair swaps as the posters. Playing the Rive half
 * live on the visitor's phone is what makes the cartoon personal without a
 * render farm: the file is one download for everyone and the child is painted
 * on arrival. See `segments.ts` for the naming that wires the chain.
 *
 * ONE Rive instance, switched between artboards with `rive.reset({artboard})`:
 * the file is parsed once, and its image slots are file-level, so the hair and
 * pupils pushed into them once dress the child in every scene. The runtime
 * fires `Load` again after every reset, which is where the fresh ViewModel
 * instance gets the look written into it. The chain's video plays in an element
 * OVER the canvas while the next artboard is already mounted behind it, so the
 * hand-over at `ended` is a `play()` and not a load.
 *
 * Three layers, bottom to top: a Rive segment's background video, the artboard,
 * the chain's own video. Sound is `CartoonMixer`; the artboards' own embedded
 * narration is muted (`rive.volume = 0`) because the same lines are delivered as
 * files and would otherwise be heard twice.
 *
 * The gate: `freeScenes` says how many scenes play before the cartoon stops
 * and asks to be unlocked — the funnel's conversion moment. Undefined plays
 * everything.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useRive,
  Fit,
  Alignment,
  Layout,
  decodeImage,
  RuntimeLoader,
  DrawOptimizationOptions,
  type FileAsset,
  type ImageAsset,
} from '@rive-app/react-webgl2';
import riveWasmUrl from '@rive-app/webgl2/rive.wasm?url';
import { loadAuthoredPalettes, type AuthoredPalettes } from '@editor/editorAssets';
import { SCENE_CHARACTER_BINDINGS, SCENE_CHARACTER_IMAGE_SLOTS, flushDataBinds } from '@editor/animationContract';
import type { FunnelLook } from '../avatarLook';
import { cartoonUrl, loadCartoonManifest, type CartoonManifest } from './manifest';
import { buildSegments, readArtboardDurations, sceneOf, type AnimationSegment } from './segments';
import { CartoonMixer } from './mixer';
import { slotBytes, slotPlan, writeLook, type LookViewModel } from './rigLook';
import { requestNarration } from './narration';

RuntimeLoader.setWasmUrl(riveWasmUrl);

/** The child's slots in the story file — authored in the Character Editor's contract. */
const CHILD_SLOTS = SCENE_CHARACTER_IMAGE_SLOTS.child;
const CHILD_VM = SCENE_CHARACTER_BINDINGS.child.rootProp;

/** A Rive artboard whose timeline length we could not read plays this long. */
const FALLBACK_RIVE_SEC = 8;
/** One advance can carry a wall-clock delta after a stall; a frame is never longer than this. */
const MAX_FRAME_SEC = 0.25;
/** How long the first Rive segment waits for the hair/pupil swaps before playing anyway. */
const SWAP_WAIT_MS = 4000;

/**
 * Make the cartoon audible on an iPhone whose ring switch is on silent.
 *
 * Web Audio (the mixer) obeys the silent switch; media playback does not.
 * Two ways to put the page into "playback" mode, both from inside the tap
 * that starts the cartoon: the Audio Session API where Safari has it (17+),
 * and, for older ones, playing a moment of silence through an <audio>
 * element, after which the page's Web Audio follows the volume buttons
 * instead of the switch. Neither has any effect on Android or desktop.
 */
const SILENT_MP3 = 'data:audio/mpeg;base64,SUQzBAAAAAAAIlRTU0UAAAAOAAADTGF2ZjYyLjMuMTAwAAAAAAAAAAAAAAD/83DAAAAAAAAAAAAASW5mbwAAAA8AAAAIAAABhwCIiIiIiIiIiIiIiIiZmZmZmZmZmZmZmZmqqqqqqqqqqqqqqqqqu7u7u7u7u7u7u7u7zMzMzMzMzMzMzMzMzN3d3d3d3d3d3d3d3e7u7u7u7u7u7u7u7u7///////////////8AAAAATGF2YzYyLjExAAAAAAAAAAAAAAAAJALUAAAAAAAAAYdlfetwAAAAAAAAAAAAAAAAAP/zEMQAAAADSAAAAABMQU1FMy4xMDBVVVVV//MSxA0AAANIAAAAAFVVVVVVVVVVVVVVVVVV//MQxBsAAANIAAAAAFVVVVVVVVVVVVVVVVX/8xDEKAAAA0gAAAAAVVVVVVVVVVVVVVVVVf/zEMQ1AAADSAAAAABVVVVVVVVVVVVVVVVV//MQxEIAAANIAAAAAFVVVVVVVVVVVVVVVVX/8xDETwAAA0gAAAAAVVVVVVVVVVVVVVVVVf/zEMRcAAADSAAAAABVVVVVVVVVVVVVVVVV';
let soundUnlocked = false;
function unlockSoundOnSilentPhone(): void {
  if (soundUnlocked) return;
  soundUnlocked = true;
  try {
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'playback';
  } catch { /* not this browser */ }
  try {
    const a = new Audio(SILENT_MP3);
    a.setAttribute('playsinline', '');
    a.volume = 0.01;
    void a.play().catch(() => { soundUnlocked = false; });
  } catch { /* not this browser */ }
}

export type PlayerPhase = 'loading' | 'idle' | 'playing' | 'paused' | 'locked' | 'ended';

/**
 * How much of the cartoon a visitor saw, reported once per viewing: when it
 * ends, when the gate stops it, or when they leave it — the tab closing, or
 * the screen it sits on going away — before either.
 */
export interface WatchReport {
  outcome: 'ended' | 'gate' | 'left';
  /** Seconds actually spent playing (pauses and buffering excluded). */
  watchedSec: number;
  /** Where in the cartoon they were, in seconds from its start. */
  positionSec: number;
  totalSec: number;
  /** positionSec / totalSec — how far into the cartoon they got. */
  watchedPct: number;
  scene: number;
  scenesTotal: number;
  /** Times the cartoon was started over after ending. */
  restarts: number;
  fullscreen: boolean;
}

export interface CartoonPlayerProps {
  /** The folder under `cartoons/` (`zoo`). */
  cartoon: string;
  look: FunnelLook;
  /** The child's name — the narration is spoken to them (see narration.ts). */
  name?: string;
  /** Scenes playable before the gate; undefined plays the whole cartoon. */
  freeScenes?: number;
  /** The gate was reached. */
  onLocked?: () => void;
  /** The gate's button. */
  onUnlock: () => void;
  /** The cartoon is not deployed here (no manifest) — show something else. */
  onUnavailable?: () => void;
  /** Phase changes, for analytics. */
  onPhase?: (phase: PlayerPhase) => void;
  /** A viewing came to an end, one way or another (see WatchReport). */
  onWatch?: (report: WatchReport) => void;
  /** A scene started playing (1-based, with how many there are). */
  onScene?: (scene: number, scenesTotal: number) => void;
  /** What the gate says. */
  lockTitle?: string;
  lockButton?: string;
}

/** A rive instance's shape this component reaches into beyond the public API. */
interface RiveHandle {
  contents?: { artboards?: { name: string; stateMachines?: (string | { name: string })[] }[] };
  viewModelInstance?: { viewModel(name: string): LookViewModel | null } & LookViewModel;
  volume: number;
  play(names?: string | string[]): void;
  pause(names?: string | string[]): void;
  reset(p: { artboard?: string; stateMachine?: string; autoplay?: boolean; autoBind?: boolean }): void;
  drawFrame(): void;
}

export function CartoonPlayer({
  cartoon, look, name, freeScenes, onLocked, onUnlock, onUnavailable, onPhase, onWatch, onScene,
  lockTitle = 'Unlock the rest of the story', lockButton = 'Watch the whole cartoon',
}: CartoonPlayerProps) {
  const [manifest, setManifest] = useState<CartoonManifest | null>(null);
  const [palettes, setPalettes] = useState<AuthoredPalettes | null>(null);
  const [phase, setPhaseState] = useState<PlayerPhase>('loading');
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [layer, setLayer] = useState<'rive' | 'video'>('rive');
  /**
   * The child is fully dressed on the poster frame. Until then the canvas is
   * hidden: the hair lands slot by slot (the back layer of a long style is
   * five large PNGs composited in a canvas, and comes last), and a poster of
   * a child with half their hair was what the first visit showed.
   */
  const [dressed, setDressed] = useState(false);

  const phaseRef = useRef<PlayerPhase>('loading');
  /**
   * The watch clock: wall time spent in the playing phase, so a pause, a
   * backgrounded tab or a stall while a video buffers does not count as
   * watching. `since` is set while playing.
   */
  const watchRef = useRef({ since: null as number | null, acc: 0, restarts: 0, reported: true, scene: 0 });
  const setPhase = useCallback((p: PlayerPhase) => {
    const prev = phaseRef.current;
    const w = watchRef.current;
    if (prev === 'playing' && p !== 'playing' && w.since !== null) { w.acc += (performance.now() - w.since) / 1000; w.since = null; }
    if (p === 'playing' && prev !== 'playing') { w.since = performance.now(); w.reported = false; }
    phaseRef.current = p;
    setPhaseState(p);
    onPhase?.(p);
  }, [onPhase]);

  const segsRef = useRef<AnimationSegment[]>([]);
  const indexRef = useRef(0);
  const onWatchRef = useRef(onWatch);
  onWatchRef.current = onWatch;
  const onSceneRef = useRef(onScene);
  onSceneRef.current = onScene;
  const fullscreenRef = useRef(false);

  /** Seconds from the cartoon's start to where playback is now. */
  const positionSec = useCallback(() => {
    const seg = segsRef.current[indexRef.current];
    if (!seg) return 0;
    const inSeg = seg.kind === 'video' ? (videoRef.current?.currentTime ?? 0) : elapsedRef.current;
    return seg.startSec + Math.min(inSeg, seg.durationSec || inSeg);
  }, []);

  /** One report per viewing; the first outcome that happens is the one. */
  const reportWatch = useCallback((outcome: WatchReport['outcome']) => {
    const w = watchRef.current;
    if (w.reported) return;
    w.reported = true;
    const segs = segsRef.current;
    const watchedSec = w.acc + (w.since !== null ? (performance.now() - w.since) / 1000 : 0);
    const totalSec = segs.reduce((t, x) => t + x.durationSec, 0);
    const position = outcome === 'ended' ? totalSec : positionSec();
    const scenes = new Set(segs.map((x) => sceneOf(x.order))).size;
    onWatchRef.current?.({
      outcome,
      watchedSec,
      positionSec: position,
      totalSec,
      watchedPct: totalSec > 0 ? Math.min(1, position / totalSec) : 0,
      scene: w.scene,
      scenesTotal: scenes,
      restarts: w.restarts,
      fullscreen: fullscreenRef.current,
    });
  }, [positionSec]);
  const elapsedRef = useRef(0);
  /** The artboard the canvas holds, and whether its Load has fired. */
  const mountedRef = useRef<{ artboard: string | null; loaded: boolean }>({ artboard: null, loaded: false });
  /** A Rive segment waiting for its artboard to finish mounting. */
  const waitingRef = useRef<string | null>(null);
  const lookRef = useRef(look);
  lookRef.current = look;
  const palettesRef = useRef<AuthoredPalettes | null>(null);
  palettesRef.current = palettes;

  const riveRef = useRef<RiveHandle | null>(null);
  /**
   * The runtime keeps the callbacks it was constructed with, so they go through
   * refs: what they call is always the latest render's, never the first's.
   */
  const onLoadRef = useRef<() => void>(() => {});
  const onAdvanceRef = useRef<(delta: number) => void>(() => {});
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgRef = useRef<HTMLVideoElement>(null);
  const mixer = useMemo(() => new CartoonMixer(), []);
  /** The narration spoken to this child, once it has landed (shot → URL). */
  const narrationRef = useRef<Record<string, string> | null>(null);
  /** Lay the personal narration over whatever chain exists by now. */
  const applyNarration = useCallback(() => {
    const lines = narrationRef.current;
    if (!lines) return;
    for (const seg of segsRef.current) {
      const m = /S(\d+)[._-](\d+)/i.exec(seg.name);
      if (!m) continue;
      const shot = `S${m[1].padStart(2, '0')}.${m[2].padStart(2, '0')}`;
      if (lines[shot]) seg.voiceOverSrc = lines[shot];
    }
    mixer.prefetch(segsRef.current.slice(indexRef.current, indexRef.current + 2).map((s) => s.voiceOverSrc));
  }, [mixer]);

  /** The child's image slots in the file, by asset name. */
  const assetMap = useRef(new Map<string, ImageAsset>());
  /** What each slot has been ASKED to hold (the plan's signature), so a look change re-swaps only what moved. */
  const appliedSlots = useRef<Record<string, string>>({});
  /** What each slot actually holds by now — the swap is async, and the poster must not show before it lands. */
  const landedSlots = useRef<Record<string, string>>({});
  /** The swaps in flight for the current look; the first Rive link waits for them. */
  const swapsDone = useRef<Promise<void>>(Promise.resolve());
  /** Failed swaps are retried a few times — a layer PNG lost under the load of the first screen is normal. */
  const swapAttempts = useRef<Record<string, number>>({});
  const applySwapsRef = useRef<() => void>(() => {});

  // ── Setup: manifest + palettes ─────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    void loadCartoonManifest(cartoon).then((m) => {
      if (!alive) return;
      if (!m) { onUnavailable?.(); return; }
      setManifest(m);
    });
    void loadAuthoredPalettes().then((p) => { if (alive) setPalettes(p); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartoon]);

  useEffect(() => () => mixer.dispose(), [mixer]);

  const rivSrc = manifest ? cartoonUrl(manifest.id, manifest.riv) : undefined;

  /** Register the child's slots as the file parses; Rive decodes the embedded originals. */
  const assetLoader = useCallback((asset: FileAsset): boolean => {
    if (asset.isImage) assetMap.current.set(asset.name, asset as ImageAsset);
    return false;
  }, []);

  // ── The look, on the freshly (re)instanced artboard ────────────────────────
  const applyLook = useCallback(() => {
    const rive = riveRef.current;
    const pal = palettesRef.current;
    if (!rive || !pal) return;
    try {
      const root = rive.viewModelInstance;
      const vmi = (CHILD_VM && root?.viewModel(CHILD_VM)) || root || null;
      writeLook(vmi, lookRef.current, pal);
      flushDataBinds(rive);
      rive.drawFrame();
    } catch {
      /* disposed mid-remount */
    }
  }, []);

  /**
   * Repaint a paused artboard so a landed image shows. `drawFrame()` does
   * nothing while a frame is already scheduled, so it is asked again on the
   * next frame — the poster stayed stale after the last hair layer landed
   * without this (2026-09-17).
   */
  const repaint = useCallback(() => {
    const r = riveRef.current;
    if (!r) return;
    try { flushDataBinds(r); r.drawFrame(); } catch { /* disposed */ }
    requestAnimationFrame(() => { try { flushDataBinds(r); r.drawFrame(); } catch { /* disposed */ } });
  }, []);

  /**
   * The image swaps: hair (recoloured stacks), pupils, freckles, glasses. Image
   * assets are FILE-level, so this runs once per file and dresses every scene.
   */
  const applySwaps = useCallback(() => {
    const rive = riveRef.current;
    const pal = palettesRef.current;
    if (!rive || !pal) return;
    const look = lookRef.current;
    const plan = slotPlan(look, pal, CHILD_SLOTS);
    let failed = false;
    const work = plan.map(async (entry) => {
      if (!entry.slot || !entry.sig || appliedSlots.current[entry.slot] === entry.sig) return;
      const asset = assetMap.current.get(entry.slot);
      if (!asset) return;
      appliedSlots.current[entry.slot] = entry.sig;
      delete landedSlots.current[entry.slot];
      try {
        const bytes = await slotBytes(entry, pal, look);
        // A newer look may have superseded this one while it built.
        if (appliedSlots.current[entry.slot] !== entry.sig) return;
        if (!bytes) throw new Error('no image built');
        const decoded = await decodeImage(bytes);
        asset.setRenderImage(decoded);
        // No `unref()` here, unlike the editing stage: on a slot whose
        // embedded image was stripped, releasing our reference after a SECOND
        // swap left the slot empty (the back hair vanished, measured
        // 2026-09-17). A look changes a handful of times per visit; the
        // handful of images kept alive is nothing.
        landedSlots.current[entry.slot] = entry.sig;
        repaint();
      } catch (err) {
        delete appliedSlots.current[entry.slot]; // so the next apply tries again
        const n = (swapAttempts.current[entry.slot] = (swapAttempts.current[entry.slot] ?? 0) + 1);
        if (import.meta.env.DEV) console.warn(`[cartoon] slot "${entry.slot}" swap failed (attempt ${n}):`, err);
        if (n < 4) failed = true;
      }
    });
    swapsDone.current = Promise.all(work).then(() => {
      if (failed) window.setTimeout(() => applySwapsRef.current(), 1500);
    });
  }, []);
  applySwapsRef.current = applySwaps;

  // The look changed under a mounted file (a re-analysis, a resumed visit).
  useEffect(() => {
    if (mountedRef.current.loaded) { applyLook(); applySwaps(); }
  }, [look, applyLook, applySwaps]);

  // ── Rive ───────────────────────────────────────────────────────────────────
  const { rive, RiveComponent } = useRive(rivSrc ? {
    src: rivSrc,
    autoplay: false,
    autoBind: true,
    assetLoader,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    drawingOptions: DrawOptimizationOptions.AlwaysDraw,
    // Fires once, for the file: the setup effect below takes it from there
    // (`rive` state lands after this), and `mountArtboard` dresses every
    // artboard instanced after it.
    onLoad: () => {},
    onAdvance: (e) => onAdvanceRef.current(typeof e.data === 'number' ? e.data : 0),
  } : null);

  /**
   * Re-instance an artboard from the loaded file. `reset()` is synchronous and
   * — unlike the first load — fires NO `Load` event (measured on webgl2 2.38:
   * only `init` emits it), so the fresh instance is dressed right here.
   */
  const mountArtboard = useCallback((seg: AnimationSegment) => {
    const r = riveRef.current;
    if (!r || mountedRef.current.artboard === seg.id) return;
    mountedRef.current = { artboard: seg.id, loaded: false };
    try {
      r.reset({ artboard: seg.id, stateMachine: seg.stateMachine, autoplay: false, autoBind: true });
    } catch {
      mountedRef.current = { artboard: null, loaded: false };
      return;
    }
    onLoadRef.current();
  }, []);

  const startRive = useCallback((seg: AnimationSegment) => {
    const r = riveRef.current;
    if (!r) return;
    waitingRef.current = null;
    elapsedRef.current = 0;
    try {
      r.volume = 0;
      r.play(seg.stateMachine);
    } catch {
      /* disposed */
    }
  }, []);

  /** A (re)instanced artboard is live: dress it, and play it if it was being waited for. */
  const onArtboardLoaded = useCallback(() => {
    mountedRef.current.loaded = true;
    try { if (riveRef.current) riveRef.current.volume = 0; } catch { /* older runtime */ }
    applyLook();
    applySwaps();
    const waiting = waitingRef.current;
    if (waiting && waiting === mountedRef.current.artboard && phaseRef.current === 'playing') {
      const seg = segsRef.current[indexRef.current];
      if (seg && seg.id === waiting) {
        // The first Rive segment gives the swaps a moment to land; later ones
        // are already dressed, because the slots are file-level.
        const swaps = swapsDone.current;
        const timeout = new Promise<void>((res) => setTimeout(res, SWAP_WAIT_MS));
        void Promise.race([swaps, timeout]).then(() => {
          if (waitingRef.current === waiting && phaseRef.current === 'playing') startRive(seg);
        });
      }
    }
  }, [applyLook, applySwaps, startRive]);

  /** `activate` is declared below; the debug handle reaches it through this. */
  const activateRef = useRef<(i: number) => void>(() => {});

  // The very first load: read the artboards, build the chain, mount its first Rive link.
  useEffect(() => {
    if (!rive || !manifest) return;
    riveRef.current = rive as unknown as RiveHandle;
    const r = riveRef.current;
    const artboards = (r.contents?.artboards ?? []).map((a) => ({
      name: a.name,
      stateMachines: (a.stateMachines ?? []).map((sm) => (typeof sm === 'string' ? sm : sm.name)),
    }));
    const durations: Record<string, number> = readArtboardDurations(rive, artboards.map((a) => a.name));
    const videos = manifest.videos.map((v) => {
      const src = cartoonUrl(manifest.id, v.name);
      if (v.duration) durations[src] = v.duration;
      return { name: v.name.replace(/\.[^.]+$/, ''), src };
    });
    const audio = manifest.audio.map((a) => ({ name: a.name.replace(/\.[^.]+$/, ''), src: cartoonUrl(manifest.id, a.name) }));
    const segs = buildSegments({ artboards, videos, audio }, durations).map((s) => (
      s.kind === 'rive' && !s.durationSec ? { ...s, durationSec: FALLBACK_RIVE_SEC } : s
    ));
    // startSec was summed before the fallback; redo it.
    let at = 0;
    for (const s of segs) { s.startSec = at; at += s.durationSec; }
    segsRef.current = segs;
    if (!segs.length) { onUnavailable?.(); return; }
    applyNarration(); // the personal lines, if they landed before the chain did

    // The poster frame: the first Rive link, dressed, paused on frame 1.
    const firstRive = segs.find((s) => s.kind === 'rive');
    if (firstRive) {
      mountedRef.current = { artboard: null, loaded: false };
      mountArtboard(firstRive);
    }
    // The first video is what PLAY starts; have it buffered.
    const firstVideo = segs.find((s) => s.kind === 'video');
    if (firstVideo?.src && videoRef.current) videoRef.current.src = firstVideo.src;
    mixer.prefetch(segs.slice(0, 2).flatMap((s) => [s.voiceOverSrc, s.sfxSrc, s.musicSrc, s.ambienceSrc]));
    setPhase('idle');
    // Dev diagnostic (also with `?debug`): the chain, the mounted artboard and
    // the live instance, e.g. __cartoon().segs.map(s => s.id).
    if (import.meta.env.DEV || new URLSearchParams(location.search).has('debug')) {
      (window as unknown as { __cartoon?: () => unknown }).__cartoon = () => ({
        rive: riveRef.current, segs: segsRef.current, mounted: mountedRef.current,
        index: indexRef.current, phase: phaseRef.current, elapsed: elapsedRef.current,
        waiting: waitingRef.current, slots: [...assetMap.current.keys()], mixer,
        applied: { ...appliedSlots.current }, landed: { ...landedSlots.current }, attempts: { ...swapAttempts.current },
        draw: () => { const r = riveRef.current; if (r) { flushDataBinds(r); r.drawFrame(); } },
        reswap: () => { appliedSlots.current = {}; applySwapsRef.current(); },
        /** Skip to a link of the chain (dev QA of a late scene). */
        jump: (i: number) => { if (phaseRef.current === 'playing') activateRef.current(i); },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rive, manifest]);

  /**
   * The narration spoken to this child. Asked for here too (the funnel asks
   * the moment the name is typed, so this is usually a cache hit), and laid
   * over the chain when it lands: every shot it names swaps its recorded
   * voice-over for the personal one — from the next shot on, if the cartoon is
   * already playing.
   */
  useEffect(() => {
    if (!name) return;
    let alive = true;
    narrationRef.current = null;
    void requestNarration(cartoon, name).then((lines) => {
      if (!alive || !lines) return;
      narrationRef.current = lines;
      // The chain may not exist yet (the file is still loading): the setup
      // effect lays the lines over it as soon as it is built.
      applyNarration();
    });
    return () => { alive = false; };
  }, [cartoon, name, applyNarration]);

  // Palettes may arrive after the artboard did.
  useEffect(() => {
    if (palettes && mountedRef.current.loaded) { applyLook(); applySwaps(); }
  }, [palettes, applyLook, applySwaps]);

  // Show the poster once the child is dressed — or after a grace period, so a
  // slot that keeps failing cannot hide the cartoon for good.
  useEffect(() => {
    if (phase !== 'idle' || dressed) return;
    let alive = true;
    const settled = () => {
      const pal = palettesRef.current;
      if (!pal) return false;
      return slotPlan(lookRef.current, pal, CHILD_SLOTS).every((e) =>
        !e.slot || !e.sig || !assetMap.current.has(e.slot) || landedSlots.current[e.slot] === e.sig);
    };
    const check = () => { if (alive && settled()) { setDressed(true); repaint(); } };
    void swapsDone.current.then(check);
    const poll = window.setInterval(check, 500);
    const grace = window.setTimeout(() => { if (alive) setDressed(true); }, SWAP_WAIT_MS * 2);
    return () => { alive = false; window.clearInterval(poll); window.clearTimeout(grace); };
  }, [phase, dressed, palettes, repaint]);

  // ── The chain ──────────────────────────────────────────────────────────────
  const activate = useCallback((i: number) => {
    const segs = segsRef.current;
    const seg = segs[i];
    if (!seg) {
      setPhase('ended');
      reportWatch('ended');
      mixer.stopAll();
      return;
    }
    if (freeScenes !== undefined && sceneOf(seg.order) > freeScenes) {
      setPhase('locked');
      reportWatch('gate');
      mixer.stopAll();
      videoRef.current?.pause();
      bgRef.current?.pause();
      onLocked?.();
      return;
    }
    indexRef.current = i;
    setIndex(i);
    elapsedRef.current = 0;
    const scene = sceneOf(seg.order);
    if (scene !== watchRef.current.scene) {
      watchRef.current.scene = scene;
      onSceneRef.current?.(scene, new Set(segs.map((x) => sceneOf(x.order))).size);
    }
    mixer.startShot(seg);
    // Buffer the next link's cues while this one plays.
    mixer.prefetch(segs.slice(i + 1, i + 3).flatMap((s) => [s.voiceOverSrc, s.sfxSrc, s.musicSrc, s.ambienceSrc]));

    const v = videoRef.current;
    if (seg.kind === 'video') {
      setLayer('video');
      if (v && seg.src) {
        if (!v.src.endsWith(seg.src) && v.src !== seg.src) v.src = seg.src;
        try { v.currentTime = 0; } catch { /* not seekable yet */ }
        void v.play().catch(() => { /* a later gesture unlocks it */ });
      }
      // Pre-mount the Rive link that follows, so the hand-over is a play().
      const nextRive = segs.slice(i + 1).find((s) => s.kind === 'rive');
      if (nextRive) mountArtboard(nextRive);
      bgRef.current?.pause();
    } else {
      setLayer('rive');
      v?.pause();
      const bg = bgRef.current;
      if (bg) {
        if (seg.backgroundSrc) {
          // Muted, so autoplay is allowed; `preload="none"` means the first
          // play() has to fetch first, and a play() that ran before the data
          // was there is retried when it is.
          if (bg.src !== seg.backgroundSrc && !bg.src.endsWith(seg.backgroundSrc)) bg.src = seg.backgroundSrc;
          try { bg.currentTime = 0; } catch { /* fine */ }
          const tryPlay = () => { if (phaseRef.current === 'playing') void bg.play().catch(() => {}); };
          bg.oncanplay = tryPlay;
          tryPlay();
        } else {
          bg.oncanplay = null;
          bg.pause();
        }
      }
      if (mountedRef.current.artboard === seg.id && mountedRef.current.loaded) {
        startRive(seg);
      } else {
        waitingRef.current = seg.id;
        mountArtboard(seg);
      }
      // The next video buffers behind the artboard.
      const nextVideo = segs.slice(i + 1).find((s) => s.kind === 'video');
      if (v && nextVideo?.src && v.src !== nextVideo.src && !v.src.endsWith(nextVideo.src)) v.src = nextVideo.src;
    }
  }, [freeScenes, mixer, mountArtboard, onLocked, reportWatch, setPhase, startRive]);

  activateRef.current = activate;

  const advance = useCallback(() => {
    if (phaseRef.current !== 'playing') return;
    activate(indexRef.current + 1);
  }, [activate]);

  /** Frames of the mounted artboard: the clock of a Rive segment. */
  const onAdvance = useCallback((delta: number) => {
    if (phaseRef.current !== 'playing') return;
    const seg = segsRef.current[indexRef.current];
    if (!seg || seg.kind !== 'rive' || mountedRef.current.artboard !== seg.id || waitingRef.current) return;
    elapsedRef.current += Math.min(Math.max(delta, 0), MAX_FRAME_SEC);
    if (elapsedRef.current >= seg.durationSec - 1 / 60) {
      try { riveRef.current?.pause(seg.stateMachine); } catch { /* disposed */ }
      advance();
    }
  }, [advance]);

  onLoadRef.current = onArtboardLoaded;
  onAdvanceRef.current = onAdvance;

  const pause = useCallback(() => {
    if (phaseRef.current !== 'playing') return;
    setPhase('paused');
    const seg = segsRef.current[indexRef.current];
    if (seg?.kind === 'video') videoRef.current?.pause();
    else { try { riveRef.current?.pause(seg?.stateMachine); } catch { /* disposed */ } }
    bgRef.current?.pause();
    mixer.pause();
  }, [mixer, setPhase]);

  const stageRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  fullscreenRef.current = fullscreen;
  /**
   * Fullscreen: the real API where it exists (iPhone got it in iOS 16.4), and
   * a fixed-position stand-in where it does not, so the button always works.
   */
  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
    const node = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    const inNative = !!(document.fullscreenElement || doc.webkitFullscreenElement);
    if (fullscreen || inNative) {
      if (inNative) { try { (document.exitFullscreen ?? doc.webkitExitFullscreen)?.call(document); } catch { /* fine */ } }
      setFullscreen(false);
      return;
    }
    const request = node.requestFullscreen ?? node.webkitRequestFullscreen;
    if (request) {
      try {
        const r = request.call(node);
        if (r && typeof (r as Promise<void>).catch === 'function') (r as Promise<void>).catch(() => setFullscreen(true));
        setFullscreen(true);
        return;
      } catch { /* fall through to the stand-in */ }
    }
    setFullscreen(true);
  }, [fullscreen]);
  /**
   * Full screen means landscape. Where the browser lets a page lock the
   * orientation (Android Chrome, in full screen) it is locked; where it does
   * not (iPhone), the stage is turned 90° with CSS while the phone is held
   * upright, and turned back the moment the phone itself is turned.
   */
  const [rotated, setRotated] = useState(false);
  useEffect(() => {
    const scr = screen as Screen & { orientation?: { lock?: (o: string) => Promise<void>; unlock?: () => void } };
    const portrait = () => window.innerHeight > window.innerWidth;
    const update = () => setRotated(fullscreen && portrait());
    if (fullscreen) {
      try { scr.orientation?.lock?.('landscape').then(update).catch(update); } catch { update(); }
    } else {
      try { scr.orientation?.unlock?.(); } catch { /* not locked */ }
      setRotated(false);
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [fullscreen]);

  useEffect(() => {
    const onChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element };
      if (!document.fullscreenElement && !doc.webkitFullscreenElement) setFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  /**
   * Keep the canvas the size of its box. The React wrapper sizes the canvas
   * once and pins it with inline pixels; it did not follow the box into full
   * screen or a narrower pane, so the artboard sat cropped in a corner.
   */
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const fit = () => {
      raf = 0;
      const r = riveRef.current as (RiveHandle & { resizeToCanvas?: () => void }) | null;
      const wrap = el.querySelector<HTMLElement>('.cartoon-rive');
      const cv = wrap?.querySelector<HTMLCanvasElement>('canvas');
      if (!r || !wrap || !cv) return;
      // The LAYOUT box, not the bounding rect: in full screen on a phone the
      // stage is rotated 90° with CSS, and the bounding rect comes back with
      // width and height swapped.
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.style.width = `${w}px`;
      cv.style.height = `${h}px`;
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
        cv.width = Math.round(w * dpr);
        cv.height = Math.round(h * dpr);
      }
      try { r.resizeToCanvas?.(); } catch { /* disposed */ }
      repaint();
    };
    const ro = new ResizeObserver(() => { if (!raf) raf = requestAnimationFrame(fit); });
    ro.observe(el);
    return () => { ro.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [repaint, rive]);

  const play = useCallback(() => {
    const p = phaseRef.current;
    if (p === 'loading' || p === 'playing' || p === 'locked') return;
    unlockSoundOnSilentPhone();
    void mixer.unlock();
    // A media element plays programmatically later only once a gesture has
    // played it; the tap that starts the cartoon is that gesture for both.
    const v = videoRef.current;
    if (p === 'paused') {
      setPhase('playing');
      const seg = segsRef.current[indexRef.current];
      if (seg?.kind === 'video') void v?.play().catch(() => {});
      else { try { riveRef.current?.play(seg?.stateMachine); } catch { /* disposed */ } }
      if (seg?.backgroundSrc) void bgRef.current?.play().catch(() => {});
      mixer.resume();
      return;
    }
    setPhase('playing');
    if (v && segsRef.current[0]?.kind !== 'video') {
      // Unlock the element inside the gesture even though a Rive link plays first.
      const unlock = v.play();
      if (unlock) unlock.then(() => v.pause()).catch(() => {});
    }
    activate(0);
  }, [activate, mixer, setPhase]);

  const restart = useCallback(() => {
    mixer.stopAll();
    setPhase('idle');
    watchRef.current.restarts += 1;
    watchRef.current.scene = 0;
    indexRef.current = 0;
    setIndex(0);
    setProgress(0);
    const first = segsRef.current.find((s) => s.kind === 'rive');
    if (first) mountArtboard(first);
    setLayer('rive');
    play();
  }, [mixer, mountArtboard, play, setPhase]);

  // A backgrounded tab stops painting; the sound must not run ahead of it.
  useEffect(() => {
    const onVis = () => { if (document.hidden) pause(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [pause]);

  // A viewing that neither ended nor hit the gate still gets its report: on
  // the tab going away, and on this player leaving the screen (a poster tap
  // takes the parent to the paywall with the cartoon mid-scene).
  useEffect(() => {
    const onHide = () => reportWatch('left');
    window.addEventListener('pagehide', onHide);
    return () => { window.removeEventListener('pagehide', onHide); reportWatch('left'); };
  }, [reportWatch]);

  // Progress, a few times a second — not per frame.
  useEffect(() => {
    if (phase !== 'playing') return;
    const total = segsRef.current.reduce((s, x) => s + x.durationSec, 0) || 1;
    const tick = () => { if (segsRef.current[indexRef.current]) setProgress(Math.min(1, positionSec() / total)); };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [phase, index, positionSec]);

  const segs = segsRef.current;
  const total = segs.reduce((s, x) => s + x.durationSec, 0);
  const label = `${Math.floor(total / 60)}:${String(Math.round(total % 60)).padStart(2, '0')}`;

  return (
    <div
      ref={stageRef}
      className={`cartoon cartoon--${phase} cartoon--${layer}${fullscreen ? ' cartoon--fs' : ''}${rotated ? ' cartoon--rotate' : ''}`}
      onClick={phase === 'playing' ? pause : undefined}
      role="region"
      aria-label="Cartoon player"
    >
      <button
        className="cartoon-fs"
        onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
        aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
        title={fullscreen ? 'Exit full screen' : 'Full screen'}
      >
        {fullscreen ? '⤡' : '⛶'}
      </button>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the bed under a Rive scene */}
      <video ref={bgRef} className="cartoon-bg" muted loop playsInline preload="none" />
      <div className="cartoon-rive" style={{ opacity: dressed ? 1 : 0, transition: 'opacity 240ms ease-out' }}>
        {rivSrc && <RiveComponent style={{ width: '100%', height: '100%' }} />}
      </div>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- sound comes from the mixer */}
      <video
        ref={videoRef}
        className="cartoon-video"
        playsInline
        preload="auto"
        onEnded={advance}
        onError={advance}
      />

      {(phase === 'idle' || phase === 'paused' || phase === 'loading') && (
        <button
          className="cartoon-play"
          onClick={(e) => { e.stopPropagation(); play(); }}
          disabled={phase === 'loading' || (phase === 'idle' && !dressed)}
          aria-label={phase === 'paused' ? 'Resume' : 'Play'}
        >
          <span className="cartoon-play-icon">▶</span>
          {(phase === 'loading' || (phase === 'idle' && !dressed)) && <span className="cartoon-play-note">Loading…</span>}
          {phase === 'idle' && dressed && total > 0 && <span className="cartoon-play-note">{label}</span>}
        </button>
      )}

      {phase === 'locked' && (
        <div className="cartoon-lock" onClick={(e) => e.stopPropagation()}>
          <strong>{lockTitle}</strong>
          <button className="cartoon-unlock" onClick={onUnlock}>{lockButton}</button>
        </div>
      )}

      {phase === 'ended' && (
        <div className="cartoon-lock" onClick={(e) => e.stopPropagation()}>
          <button className="cartoon-unlock" onClick={restart}>↻ Watch again</button>
        </div>
      )}

      <div className="cartoon-bar" aria-hidden>
        <span style={{ transform: `scaleX(${progress})` }} />
      </div>
    </div>
  );
}
